import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { addApplication, JobApplication } from '../redux/jobSlice';
import { TextField, Button, Container, Typography, Box, MenuItem, FormControl, Paper, Snackbar, Alert, Select, InputLabel, SelectChangeEvent, Autocomplete } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { debounce } from 'lodash';
import { MAPBOX_ACCESS_TOKEN } from '../mapboxConfig';
import ImportApplications from '../components/ImportApplications'; // Add this import

const AddApplication: React.FC = () => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState<Omit<JobApplication, 'id'>>({
    company: '',
    position: '',
    location: '',
    salary: '',
    salaryType: 'yearly',
    hoursPerWeek: 40,
    status: 'Applied',
    dateApplied: '',
    link: '',
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [query, setQuery] = useState('');

  const fetchSuggestions = async (query: string) => {
    const endpoint = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`;
    const params = new URLSearchParams({
      access_token: MAPBOX_ACCESS_TOKEN || '',
      types: 'place',
      limit: '5',
    });

    const response = await fetch(`${endpoint}?${params}`);
    const data = await response.json();
    return data.features.map((feature: any) => feature.place_name);
  };

  const [locationSuggestions, setLocationSuggestions] = useState<string[]>([]);

  const debouncedFetchSuggestions = useCallback(
    debounce(async (input: string) => {
      if (input.length > 2) {
        const suggestions = await fetchSuggestions(input);
        setLocationSuggestions(suggestions);
      } else {
        setLocationSuggestions([]);
      }
    }, 300),
    []
  );

  const handleLocationChange = (event: React.ChangeEvent<{}>, value: string | null) => {
    const location = value === 'Remote' || value === 'N/A' ? value : (value || '');
    setFormData(prev => ({ ...prev, location }));

  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'hoursPerWeek' ? Number(value) : value
    }));
    if (name === 'location') {
      setQuery(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const applicationData = {
        ...formData,
        dateApplied: formData.dateApplied ? new Date(formData.dateApplied).toISOString() : new Date().toISOString(),
      };

      console.log('Sending application data:', applicationData); // Add this line for debugging

      // const response = await fetch('http://localhost:5001/api/applications', {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': localStorage.getItem('token') || '',
        },
        body: JSON.stringify(applicationData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to add application: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      console.log('Add application response:', data);

      setFormData({
        company: '',
        position: '',
        location: '',
        salary: '',
        salaryType: 'yearly',
        hoursPerWeek: 40,
        status: 'Applied',
        dateApplied: '',
        link: '',
      });
      setSnackbarMessage('Application added successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error adding application:', error);
      setSnackbarMessage(error instanceof Error ? error.message : 'An error occurred while adding the application');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#3a7bd5' }}>
            Add Job Application
          </Typography>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              label="Company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Position"
              name="position"
              value={formData.position}
              onChange={handleChange}
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Autocomplete
              freeSolo
              options={['Remote', 'N/A', ...locationSuggestions]}
              onInputChange={(event, newInputValue) => {
                if (newInputValue !== 'Remote' && newInputValue !== 'N/A') {
                  debouncedFetchSuggestions(newInputValue);
                }
              }}
              onChange={handleLocationChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  margin="normal"
                  label="Location"
                  name="location"
                  required
                  helperText="Enter city, state, 'Remote', or 'N/A'"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
              value={formData.location}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Salary"
              name="salary"
              value={formData.salary}
              onChange={handleChange}
              helperText="Enter salary amount"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <FormControl fullWidth margin="normal">
              <TextField
                select
                fullWidth
                label="Salary Type"
                name="salaryType"
                value={formData.salaryType}
                onChange={handleChange}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: 2 },
                  '& .MuiInputBase-root': { height: '56px' },
                }}
              >
                <MenuItem value="yearly">Yearly</MenuItem>
                <MenuItem value="hourly">Hourly</MenuItem>
              </TextField>
            </FormControl>
            {formData.salaryType === 'hourly' && (
              <TextField
                fullWidth
                margin="normal"
                label="Hours per Week"
                name="hoursPerWeek"
                type="number"
                value={formData.hoursPerWeek}
                onChange={handleChange}
                helperText="Enter average hours worked per week"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            )}
            <FormControl fullWidth margin="normal">
              <TextField
                select
                fullWidth
                margin="normal"
                label="Status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                required
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              >
                <MenuItem value="Applied">Applied</MenuItem>
                <MenuItem value="Interviewing">Interviewing</MenuItem>
                <MenuItem value="Hired">Hired</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
              </TextField>
            </FormControl>
            <DatePicker
              label="Date Applied"
              value={formData.dateApplied ? dayjs(formData.dateApplied) : null}
              onChange={(date) => setFormData(prev => ({ ...prev, dateApplied: date ? date.format('YYYY-MM-DD') : '' }))}
              sx={{
                width: '100%',
                mt: 2,
                mb: 1,
                '& .MuiOutlinedInput-root': { borderRadius: 2 },
                '& .MuiInputBase-root': {
                  background: 'linear-gradient(135deg, #ffffff 0%, #f0f4f8 100%)',
                },
                '& .MuiInputBase-input': { color: '#3a7bd5' },
                '& .MuiIconButton-root': { color: '#3a7bd5' },
              }}
            />
            <TextField
              fullWidth
              margin="normal"
              label="Job Link"
              name="link"
              value={formData.link}
              onChange={handleChange}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
                background: 'linear-gradient(to right, #3a7bd5, #00d2ff)',
                '&:hover': {
                  background: 'linear-gradient(to right, #3a7bd5, #3a7bd5)',
                },
              }}
            >
              Add Application
            </Button>
          </form>
        </Paper>
        <Box mt={4}>
          <ImportApplications />
        </Box>
      </Container>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </LocalizationProvider>

  );
};

export default AddApplication;